import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Truck, Radio, FileText, Container, Wrench, Clock } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatCard, StatusBadge } from "../components/common";

export default function Dashboard() {
  const { tenant, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [deleted, setDeleted] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (["admin", "shop_manager", "dispatcher"].includes(user?.role)) {
      api.get("/audit-log").then((r) => setDeleted(r.data)).catch(() => {});
    }
  }, [user?.role]);

  const fmtDate = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Operations Dashboard" subtitle={tenant ? `${tenant.name} — live module telemetry` : ""} testId="dashboard-header">
        {tenant && <StatusBadge status={tenant.status} testId="dashboard-tenant-status" />}
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
        <StatCard label="Customers" value={stats?.customers ?? "—"} icon={Users} testId="stat-customers" />
        <StatCard label="Assets on Ledger" value={stats?.assets ?? "—"} icon={Truck} testId="stat-assets" />
        <StatCard label="In Shop" value={stats?.assets_in_shop ?? "—"} icon={Wrench} accent="text-red-400" testId="stat-in-shop" />
        {stats?.swd && (
          <>
            <StatCard label="Active Dispatches" value={stats.swd.open_dispatches} icon={Radio} accent="text-blue-400" testId="stat-open-dispatches" />
            <StatCard label="Open Job Tickets" value={stats.swd.open_tickets} icon={FileText} accent="text-amber-400" testId="stat-open-tickets" />
            <StatCard label="Job Tickets" value={stats.swd.total_tickets} icon={FileText} accent="text-sky-400" testId="stat-total-tickets" />
            <StatCard label="Driver Hours" value={stats.swd.total_hours} icon={Clock} accent="text-emerald-400" testId="stat-hours" />
          </>
        )}
        {stats?.fractank && (
          <>
            <StatCard label="Units On Rent" value={stats.fractank.active_rentals} icon={Container} accent="text-emerald-400" testId="stat-active-rentals" />
            <StatCard label="Pending Field Orders" value={stats.fractank.pending_orders} icon={FileText} accent="text-orange-400" testId="stat-pending-orders" />
          </>
        )}
        {stats?.fleet && (
          <StatCard label="Open Work Orders" value={stats.fleet.open_work_orders} icon={Wrench} accent="text-red-400" testId="stat-open-work-orders" />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <div className="bg-card border border-slate-700/60 rounded-lg p-6">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-4">Enabled Modules</p>
          <div className="flex flex-wrap gap-2" data-testid="dashboard-modules">
            {tenant?.enabled_modules?.map((m) => (
              <span key={m} className="px-3 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-mono uppercase tracking-wider">
                {m === "swd" ? "Hauling Logic" : m === "fractank" ? "Rental Logic" : m === "fleet" ? "Fleet Logic" : "Accounting Logic"}
              </span>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-4 leading-relaxed">
            Modules are provisioned per tenant by the platform owner and billed on the {tenant?.plan} plan. Additional business lines can be added without touching shared customer or asset data.
          </p>
        </div>
        <div className="bg-card border border-slate-700/60 rounded-lg p-6">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-4">Quick Actions</p>
          <div className="grid grid-cols-2 gap-3">
            {tenant?.enabled_modules?.includes("swd") && (
              <>
                <Link data-testid="quick-new-dispatch" to="/swd/dispatch" className="px-4 py-3 rounded-md bg-slate-800/60 border border-slate-700 text-sm text-slate-200 hover:border-amber-500/40 hover:text-amber-300 transition-colors duration-200">New Hauling Dispatch</Link>
                <Link data-testid="quick-gps-checkin" to="/swd/map" className="px-4 py-3 rounded-md bg-slate-800/60 border border-slate-700 text-sm text-slate-200 hover:border-amber-500/40 hover:text-amber-300 transition-colors duration-200">GPS Check-in</Link>
              </>
            )}
            {tenant?.enabled_modules?.includes("fractank") && (
              <>
                <Link data-testid="quick-frac-order" to="/fractank/orders" className="px-4 py-3 rounded-md bg-slate-800/60 border border-slate-700 text-sm text-slate-200 hover:border-amber-500/40 hover:text-amber-300 transition-colors duration-200">Field Rental Order</Link>
                <Link data-testid="quick-mobile-rental" to="/mobilerental" className="px-4 py-3 rounded-md bg-slate-800/60 border border-slate-700 text-sm text-slate-200 hover:border-amber-500/40 hover:text-amber-300 transition-colors duration-200">Mobile Rental</Link>
                <Link data-testid="quick-mobile-driver" to="/mobiledriver" className="px-4 py-3 rounded-md bg-slate-800/60 border border-slate-700 text-sm text-slate-200 hover:border-amber-500/40 hover:text-amber-300 transition-colors duration-200">Mobile Driver</Link>
              </>
            )}
            {tenant?.enabled_modules?.includes("fleet") && (
              <Link data-testid="quick-work-order" to="/fleet/work-orders" className="px-4 py-3 rounded-md bg-slate-800/60 border border-slate-700 text-sm text-slate-200 hover:border-amber-500/40 hover:text-amber-300 transition-colors duration-200">Open Work Order</Link>
            )}
          </div>
        </div>
      </div>

      {deleted.length > 0 && (
        <div className="bg-card border border-slate-700/60 rounded-lg p-6 mt-6" data-testid="dashboard-recently-deleted">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-4">Recently Deleted</p>
          <div className="space-y-2">
            {deleted.slice(0, 8).map((a) => (
              <div key={a.id} className="flex min-w-0 flex-col gap-1 text-xs font-mono sm:flex-row sm:items-start sm:justify-between sm:gap-4" data-testid={`audit-row-${a.id}`}>
                <span data-testid={`audit-label-${a.id}`} className="min-w-0 text-slate-200 [overflow-wrap:anywhere]">{a.label}{a.detail ? <span className="text-slate-500"> — {a.detail}</span> : null}</span>
                <span data-testid={`audit-meta-${a.id}`} className="min-w-0 text-slate-500 [overflow-wrap:anywhere] sm:text-right">deleted by {a.user_name} · {fmtDate(a.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
